import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAbonnement, useAbonnementActions } from '../hooks/useAbonnements';
import abonnementService from '../services/abonnementService';
import '../styles/EditAbonnement.css';

const EditAbonnement = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  
  // Mapper les anciens IDs numériques vers les UUIDs réels
  const getUUIDFromLegacyId = (legacyId) => {
    const mapping = {
      '1': '9dbc674f-9d61-41cc-92b3-061cda22d99e', // Standard -> Basic
      '2': '26c21b51-39d8-4017-9b37-8f8849a51c64', // Premium -> Standard  
      '3': 'c673b658-3867-4b1b-9d9b-30ab08b5160e'  // Enterprise -> Premium
    };
    return mapping[legacyId] || legacyId;
  };
  
  const actualId = getUUIDFromLegacyId(id);
  const { plan, loading: planLoading, error: planError } = useAbonnement(actualId);
  const { updatePlan, loading } = useAbonnementActions();

  const [formData, setFormData] = useState({
    name: '',
    price: '',
    billing_period: 'monthly'
  });

  const [features, setFeatures] = useState(abonnementService.getDefaultFeatures());
  const [error, setError] = useState('');

  useEffect(() => {
    if (plan) {
      setFormData({
        name: plan.name || '',
        price: plan.price?.toString() || '',
        billing_period: plan.billing_period || 'monthly'
      });

      // Mettre à jour les features avec les données du plan
      const planFeatures = abonnementService.formatFeatures(plan.features || {});
      setFeatures(planFeatures);
    }
  }, [plan]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFeatureChange = (featureId) => {
    setFeatures(prev => 
      prev.map(feature => 
        feature.id === featureId 
          ? { ...feature, checked: !feature.checked }
          : feature
      )
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name || !formData.price) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      const planData = {
        ...formData,
        price: parseFloat(formData.price),
        features: abonnementService.featuresToObject(features),
        max_missions_per_month: plan?.max_missions_per_month || null,
        commission_rate: plan?.commission_rate || 0
      };

      await updatePlan(actualId, planData);
      navigate('/abonnements');
    } catch (error) {
      setError('Erreur lors de la mise à jour du plan');
    }
  };

  const handleCancel = () => {
    navigate('/abonnements');
  };

  if (planLoading) {
    return <div className="loading">Chargement du plan...</div>;
  }

  if (planError) {
    return <div className="error-message">Erreur: {planError}</div>;
  }

  if (!plan) {
    return <div className="error-message">Plan non trouvé</div>;
  }

  return (
    <div className="edit-abonnement-container">
      <div className="edit-abonnement-header">
        <button className="back-button" onClick={handleCancel}>
          ← Modifier abonnement
        </button>
      </div>

      <div className="edit-content">
        <div className="plan-preview">
          <div className="plan-preview-card">
            <h3>{formData.name || 'Basic'}</h3>
            <div className="plan-price">{formData.price ? `${formData.price} FCFA` : '1 900 FCFA'}</div>
            
            <div className="plan-duration">
              Durée : {formData.billing_period === 'monthly' ? 'Mensuelle' : 
                      formData.billing_period === 'quarterly' ? 'Trimestrielle' : 
                      'Annuelle'}
            </div>

            <div className="plan-services">
              <strong>Services inclus :</strong>
              <ul>
                {features.filter(f => f.checked && ['one_service_type', 'up_to_3_services', 'priority_display', 'map_visibility', 'email_support', 'unlimited_services', 'premium_visibility', 'complete_dashboard', 'verified_badge'].includes(f.id)).map((feature) => (
                  <li key={feature.id}>✓ {feature.label}</li>
                ))}
              </ul>
            </div>

            <div className="plan-limits">
              <strong>Limites :</strong>
              <ul>
                {features.filter(f => f.checked && ['monthly_limit_30', 'quarterly_limit', 'monthly_limit_150', 'monthly_limit_500', 'one_vehicle_only', 'up_to_3_vehicles', 'up_to_10_vehicles', 'unlimited_reservations', 'limited_stats', 'no_advanced_stats'].includes(f.id)).map((feature) => (
                  <li key={feature.id}>✓ {feature.label}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="edit-form">
          <h3>Offre</h3>
          <div className="features-list">
            {features.map((feature) => (
              <label key={feature.id} className="feature-checkbox">
                <input
                  type="checkbox"
                  checked={feature.checked}
                  onChange={() => handleFeatureChange(feature.id)}
                />
                <span className="checkmark"></span>
                {feature.label}
              </label>
            ))}
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={handleCancel}>
              Annuler
            </button>
            <button 
              type="button" 
              className="btn-submit" 
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditAbonnement;